import React from 'react'
import { gql } from '@apollo/client'
import map from 'lodash/map'
import type { Logistic, RouteDef } from '@ztechapps/common.contract'
import { useDocument } from '@ztechapps/common.hook'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
import { LocalDate } from '@ztechapps/webui.date'
import { getCompanyOrProfileName } from '@ztechapps/common.utils'
import { buildLogisticRouteLabel } from '@ztechapps/logistic.common'
import { EntityCodeLink, EntityPreview, ShowIf } from '@ztechapps/webui.util'
import { LogisticStatus, LogisticStatusBadge } from '../LogisticStatus/LogisticStatusBadge'

const logisticPreviewQuery = gql`
  query singleLogistic($filter: JSON) {
    singleLogistic(filter: $filter) {
      _id
      code
      status
      shipDate
      expectedFinishDate
      vendor {
        _id
        company {
          _id
          name
        }
        profile {
          _id
          name
        }
      }
      shipTo {
        _id
        company {
          _id
          name
        }
        profile {
          _id
          name
        }
      }
      items {
        _id
        product {
          _id
          code
          name
        }
        quantity
      }
    }
  }
`

const LogisticPreview = ({ _id, code }: { _id: string; code: string }) => {
  const logisticDocumentResult = useDocument<Logistic>({ _id, query: logisticPreviewQuery })
  const logistic = logisticDocumentResult.document

  return (
    <EntityPreview
      code={code}
      statusBadge={<LogisticStatusBadge status={logistic?.status} />}
      loading={logisticDocumentResult.loading}
      error={logisticDocumentResult.error}
      title={buildLogisticRouteLabel(
        getCompanyOrProfileName(logistic?.vendor),
        getCompanyOrProfileName(logistic?.shipTo)
      )}
      rows={[
        // `LocalDate` always renders SOMETHING (its own "--" for a missing date), so
        // `EntityPreview`'s `?? '---'` fallback never sees a falsy value to catch — checked here
        // instead, same as `WorkOrderCodeLink`'s Tags row.
        { label: 'Ship Date', value: logistic?.shipDate ? <LocalDate value={logistic?.shipDate} /> : undefined },
        {
          label: 'Expected Finish',
          value: logistic?.expectedFinishDate ? <LocalDate value={logistic?.expectedFinishDate} /> : undefined,
        },
      ]}
      // Entry shape follows `PurchaseOrderCodeLink.tsx:85-92` — `code - name [quantity]`, without
      // its unit symbol: a `LogisticItem` carries no `uom`
      // (`libs/common/contract/src/logistic/LogisticItem.ts:18-20`).
      list={{
        label: 'Items',
        items: map(logistic?.items, (item) => (
          <>
            {item?.product?.code} - {item?.product?.name} [{item?.quantity}]
          </>
        )),
      }}
    />
  )
}

export type LogisticCodeLinkProps = { _id: string; code: string; status?: string }

// `status` is optional — mirrors `OrderCodeLink`/`PurchaseOrderCodeLink`: a caller that only
// has `_id`/`code` on hand renders the code link without the status dot (an empty status would
// otherwise fall through to the default info dot); the hover preview fetches the full document
// — status included — on its own.
export const LogisticCodeLink = ({ _id, code, status }: LogisticCodeLinkProps) => {
  const logisticItemRoute = useInject<RouteDef>(WebuiIocKey.routes.logisticItem)

  return (
    <EntityCodeLink
      code={code}
      statusDot={
        <ShowIf condition={!!status}>
          <LogisticStatus status={status ?? ''} />
        </ShowIf>
      }
      to={logisticItemRoute.altPath({ _id })}
      target="_blank"
      preview={<LogisticPreview _id={_id} code={code} />}
    />
  )
}
